import { Component, OnInit, Input, ViewChild, EventEmitter, Output, HostListener } from "@angular/core";
import { EnvironmentConfig, ImageUtils, ModalService } from "../../../core/core";
import { ToastrService } from "ngx-toastr";
import { forkJoin, from, of } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { InventoryService } from "../../services/inventory.service";
import { DomSanitizer } from "@angular/platform-browser";
import Cropper from "cropperjs/dist/cropper.esm.js";
import { EditInventoryImagesComponent } from "../edit-inventory-images/edit-inventory-images.component";

declare var environment: EnvironmentConfig;

@Component({
  selector: "common-inventory-images",
  templateUrl: "./inventory-images.component.html",
  styleUrls: ["./inventory-images.component.scss"],
})
export class InventoryImagesComponent implements OnInit {
  @Input() inventoryItem: any;
  @Input() showAdminActions: boolean = true;
  @Input() allowBatchUpload: boolean = false;

  @Output() imageUploaded = new EventEmitter<number>();
  @Output() imageDeleted = new EventEmitter<any>();

  images = [
    { name: "Machine Listing Photos #1" },
    { name: "Machine Listing Photos #2" },
    { name: "Machine Listing Photos #3" },
    { name: "Machine Listing Photos #4" },
    { name: "Machine Listing Photos #5" },
    { name: "Machine Listing Photos #6" },
    { name: "Machine Listing Photos #7" },
    { name: "Machine Listing Photos #8" },
  ];

  imagePaths: string[] = [];
  imageFiles: any[] = [];
  indexForSingle: number;

  @ViewChild("modalContent") modalContent: HTMLElement;
  @ViewChild("croppr") croppr: HTMLElement;
  imgCropperSrc: string;
  cropInstance: any;
  imagesCollectionForEditing: any[];

  /** Which listing photo slot is currently highlighted as a drop target. */
  dragOverSlotIndex: number | null = null;

  private pendingClientListingUploadSlot: number | null = null;

  listingImagesLoaded = false;

  constructor(
    private inventoryService: InventoryService,
    private toastrService: ToastrService,
    private domSanitizer: DomSanitizer,
    private modalService: ModalService,
  ) {
    this.imagePaths = Array(this.images.length).fill("");
    this.imagesCollectionForEditing = Array(this.images.length).fill(null);
  }

  @HostListener("document:dragend")
  onDocumentDragEnd() {
    this.dragOverSlotIndex = null;
  }

  @HostListener("document:dragover", ["$event"])
  onDocumentDragOver(event: DragEvent) {
    if (this.isFileDragEvent(event)) {
      event.preventDefault();
    }
  }

  @HostListener("document:drop", ["$event"])
  onDocumentDrop(event: DragEvent) {
    if (this.isFileDragEvent(event)) {
      event.preventDefault();
    }
  }

  ngOnInit() {
    this.listingImagesLoaded = false;
    this.inventoryService.getImages(this.inventoryItem.id, this.inventoryItem.accountId).subscribe({
      next: result => {
        this.pendingClientListingUploadSlot = null;
        this.indexForSingle = null;
        const slots = this.buildListingImageSlots(result);
        this.imagePaths = slots.paths;
        this.imagesCollectionForEditing = slots.metadata;
        this.listingImagesLoaded = true;
        this.emitUploadedCount();
      },
      error: () => {
        this.listingImagesLoaded = true;
        this.emitUploadedCount();
      },
    });
  }

  onMultipleImagesChange(event: Event) {
    const images = this.getSelectedFiles(event);
    if (!images.length) {
      return;
    }
    if (!this.requireListingImagesLoaded()) {
      return;
    }

    const availableSlots = this.getAvailableSlots();
    if (!this.canProceedWithBatchUpload(images, availableSlots)) {
      return;
    }

    this.uploadFilesToSlots(images, availableSlots);
  }

  setIndexForSingle(index) {
    this.indexForSingle = index;
  }

  onSingleImageChange(event) {
    const files = event.target.files;
    if (!files?.length) {
      return;
    }
    if (!this.requireListingImagesLoaded()) {
      event.target.value = null;
      return;
    }
    this.beginSingleImageWorkflow(files[0]);
    event.target.value = null;
  }

  onListingSlotDragOver(event: DragEvent, slotIndex: number) {
    if (this.isSlotOccupied(slotIndex)) {
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = "none";
      }
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
    this.dragOverSlotIndex = slotIndex;
  }

  onListingSlotDragLeave(event: DragEvent, slotIndex: number) {
    event.preventDefault();
    const next = event.relatedTarget as Node | null;
    const current = event.currentTarget as HTMLElement | null;
    if (!current || (next && current.contains(next))) {
      return;
    }
    if (this.dragOverSlotIndex === slotIndex) {
      this.dragOverSlotIndex = null;
    }
  }

  onListingSlotDrop(event: DragEvent, slotIndex: number) {
    event.preventDefault();
    event.stopPropagation();
    this.dragOverSlotIndex = null;

    const files = event.dataTransfer?.files;
    if (!files?.length) {
      return;
    }

    const imageFile = Array.from(files).find((f: File) => f.type.startsWith("image/"));
    if (!imageFile) {
      this.toastrService.error("Please drop an image file.");
      return;
    }

    if (files.length > 1) {
      this.toastrService.info("Multiple files were dropped; only the first image will be used for this slot.");
    }

    if (!this.requireListingImagesLoaded()) {
      return;
    }

    if (this.isSlotOccupied(slotIndex)) {
      this.toastrService.info("This slot already has a photo. Delete the existing photo to replace it.");
      return;
    }

    this.setIndexForSingle(slotIndex);
    this.beginSingleImageWorkflow(imageFile);
  }

  private beginSingleImageWorkflow(file: File) {
    if (this.indexForSingle == null) {
      return;
    }
    this.imageFiles[this.indexForSingle] = file;
    ImageUtils.resizeImage({
      file,
      maxSize: 1600,
    })
      .then((resizedImage: Blob) => {
        const objectUrl = window.URL.createObjectURL(resizedImage);
        this.imgCropperSrc = this.domSanitizer.bypassSecurityTrustUrl(objectUrl) as any;
        this.modalService.largeModal(this.modalContent, true);
      })
      .catch(err => {
        console.error(err);
      });
  }

  imageLoadedForCropper(event) {
    this.cropInstance = new Cropper(event.target, {
      viewMode: 1,
      dragMode: "move",
      aspectRatio: 16 / 9,
      restore: false,
      guides: true,
      center: false,
      highlight: true,
      background: false,
      cropBoxMovable: false,
      cropBoxResizable: false,
      toggleDragModeOnDblclick: false,
    });

    setTimeout(() => {
      const containerData = this.cropInstance.getContainerData();
      this.cropInstance.zoomTo(0, {
        x: containerData.width / 2,
        y: containerData.height / 2,
      });
    }, 800);
  }

  zoomOut() {
    this.cropInstance.zoom(-0.1);
  }

  zoomIn() {
    this.cropInstance.zoom(0.1);
  }

  saveCroppedPhoto(modal) {
    const slot = this.indexForSingle;
    if (slot == null) {
      return;
    }
    this.cropInstance.getCroppedCanvas().toBlob(blob => {
      this.pendingClientListingUploadSlot = slot;
      const objectUrl = window.URL.createObjectURL(blob);
      this.imagePaths[slot] = this.domSanitizer.bypassSecurityTrustUrl(objectUrl) as any;
      var file = new File([blob], this.imageFiles[slot].name);
      this.imageFiles[slot] = file;
      modal.close();
    });
  }

  imgOnLoad(event: Event, slotIndex: number) {
    if (
      this.pendingClientListingUploadSlot == null ||
      this.pendingClientListingUploadSlot !== slotIndex ||
      this.indexForSingle !== slotIndex
    ) {
      return;
    }

    const target = event.target as HTMLImageElement;
    if (target.width < target.height) {
      this.imagePaths[slotIndex] = null;
      this.imageFiles[slotIndex] = null;
      this.pendingClientListingUploadSlot = null;
      this.indexForSingle = null;
      alert("Landscape(horizontal) photo is required for listing photos. Please step back and rotate your phone while taking the photo.");
      return;
    }

    this.uploadImage(this.imageFiles[slotIndex], slotIndex);
  }

  uploadImage(image, index) {
    this.inventoryService.uploadImage(this.inventoryItem.id, this.inventoryItem.accountId, index, image).subscribe({
      next: () => {
        this.toastrService.success("Successfully uploaded photo");
        this.imagesCollectionForEditing[index] = { name: image?.name || this.images[index].name };
        this.emitUploadedCount();
        this.pendingClientListingUploadSlot = null;
        this.indexForSingle = null;
      },
      error: () => {
        this.imagePaths[index] = "";
        this.imageFiles[index] = null;
        this.pendingClientListingUploadSlot = null;
        this.indexForSingle = null;
        this.emitUploadedCount();
        this.toastrService.error("Upload failed. Please try again.");
      },
    });
  }

  updateCoverImage(index) {
    this.inventoryService.updateCoverImage(this.inventoryItem.id, this.inventoryItem.accountId, index).subscribe(result => {
      this.inventoryItem.coverImagePosition = index;
      this.toastrService.success("Successfully set cover photo");
    });
  }

  // dont need this anymore since its only in market
  editImage(image, index) {
    const slot = index;
    this.indexForSingle = slot;
    const modalRef = this.modalService.largeModal(EditInventoryImagesComponent, true);
    modalRef.componentInstance.originalImageSource = this.imagePaths[slot];

    modalRef.result.then(
      blob => {
        this.pendingClientListingUploadSlot = slot;
        const objectUrl = window.URL.createObjectURL(blob);
        this.imagePaths[slot] = this.domSanitizer.bypassSecurityTrustUrl(objectUrl) as any;
        const originalName = this.imagesCollectionForEditing?.[slot]?.name || this.images[slot].name;
        var file = new File([blob], originalName);
        this.imageFiles[slot] = file;
      },
      () => {
        if (this.indexForSingle === slot) {
          this.indexForSingle = null;
        }
      },
    );
  }

  deleteImage(index) {
    this.inventoryService.deleteImage(this.inventoryItem.id, this.inventoryItem.accountId, index).subscribe(result => {
      this.imagePaths[index] = "";
      this.imagesCollectionForEditing[index] = null;
      this.toastrService.success("Successfully deleted photo");
      this.emitUploadedCount();
      this.imageDeleted.emit();
    });
  }

  private requireListingImagesLoaded(): boolean {
    if (!this.listingImagesLoaded) {
      this.toastrService.warning("Listing photos are still loading. Please wait a moment.");
      return false;
    }
    return true;
  }

  private isSlotOccupied(index: number): boolean {
    const value = this.imagePaths[index];
    return value != null && value !== "";
  }

  private isFileDragEvent(event: DragEvent): boolean {
    const types = event.dataTransfer?.types;
    if (!types) {
      return false;
    }
    for (let i = 0; i < types.length; i++) {
      if (types[i] === "Files") {
        return true;
      }
    }
    return false;
  }

  private getSelectedFiles(event: Event): File[] {
    const input = event.target as HTMLInputElement;
    const files: File[] = Array.from(input?.files || []);
    if (input) {
      input.value = null;
    }
    return files;
  }

  private getAvailableSlots(): number[] {
    return this.images.map((_, index) => index).filter(index => !this.isSlotOccupied(index));
  }

  private canProceedWithBatchUpload(images: File[], availableSlots: number[]): boolean {
    if (availableSlots.length === 0) {
      this.toastrService.info("All listing photo slots are full. Delete a photo to upload another.");
      return false;
    }

    if (images.length > availableSlots.length) {
      this.toastrService.error(
        `You selected ${images.length} photos, but only ${availableSlots.length} upload slot(s) are available.`,
      );
      return false;
    }

    if (images.some(file => !file.type?.startsWith("image/"))) {
      this.toastrService.error("Only image files are allowed.");
      return false;
    }

    return true;
  }

  private uploadFilesToSlots(images: File[], availableSlots: number[]): void {
    const uploadRequests = images.map((image, index) => {
      const slotIndex = availableSlots[index];
      return from(this.prepareBatchListingFile(image)).pipe(
        switchMap(preparedImage =>
          this.inventoryService.uploadImage(this.inventoryItem.id, this.inventoryItem.accountId, slotIndex, preparedImage).pipe(
            tap(result => {
              this.imagePaths[slotIndex] = result.fullSize;
              this.imageFiles[slotIndex] = preparedImage;
              this.imagesCollectionForEditing[slotIndex] = { name: image.name };
            }),
            map(() => ({ ok: true as const })),
          ),
        ),
        catchError(error => of({ ok: false as const, landscapeRejected: error?.message === "LANDSCAPE_REQUIRED" })),
      );
    });

    forkJoin(uploadRequests).subscribe({
      next: (results: Array<{ ok: boolean; landscapeRejected?: boolean }>) => {
        const succeeded = results.filter(r => r.ok).length;
        const failed = results.length - succeeded;
        const landscapeRejected = results.filter(r => !r.ok && r.landscapeRejected).length;
        const retryableFailures = failed - landscapeRejected;
        this.emitUploadedCount();
        if (failed === 0) {
          this.toastrService.success(`Successfully uploaded ${succeeded} photo${succeeded === 1 ? "" : "s"}.`);
        } else if (succeeded === 0) {
          if (landscapeRejected > 0) {
            this.toastrService.error("Portrait photos were skipped. Landscape(horizontal) photos are required.");
          } else {
            this.toastrService.error("Upload failed. Please try again.");
          }
        } else {
          if (retryableFailures > 0 && landscapeRejected > 0) {
            this.toastrService.warning(
              `${succeeded} photo${succeeded === 1 ? "" : "s"} uploaded. ${retryableFailures} failed and can be retried; ${landscapeRejected} ${landscapeRejected === 1 ? "was" : "were"} skipped because only landscape(horizontal) photos are allowed.`,
            );
          } else if (retryableFailures > 0) {
            this.toastrService.warning(
              `${succeeded} photo${succeeded === 1 ? "" : "s"} uploaded, but ${retryableFailures} failed. Try uploading the failed photo(s) again.`,
            );
          } else {
            this.toastrService.info(
              `${succeeded} photo${succeeded === 1 ? "" : "s"} uploaded. ${landscapeRejected} ${landscapeRejected === 1 ? "was" : "were"} skipped because only landscape(horizontal) photos are allowed.`,
            );
          }
        }
      },
      error: () => {
        this.emitUploadedCount();
        this.toastrService.error("Upload failed. Please try again.");
      },
    });
  }

  private async prepareBatchListingFile(file: File): Promise<File> {
    const resizedImage = await ImageUtils.resizeImage({
      file,
      maxSize: 1600,
    });
    const sourceImage = await this.loadImageFromBlob(resizedImage);
    if (sourceImage.naturalWidth < sourceImage.naturalHeight) {
      throw new Error("LANDSCAPE_REQUIRED");
    }
    const croppedImage = await this.cropToSixteenByNine(sourceImage);
    return new File([croppedImage], file.name, { type: "image/jpeg" });
  }

  private loadImageFromBlob(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const objectUrl = window.URL.createObjectURL(blob);
      const image = new Image();
      image.onload = () => {
        window.URL.revokeObjectURL(objectUrl);
        resolve(image);
      };
      image.onerror = () => {
        window.URL.revokeObjectURL(objectUrl);
        reject(new Error("INVALID_IMAGE"));
      };
      image.src = objectUrl;
    });
  }

  private cropToSixteenByNine(image: HTMLImageElement): Promise<Blob> {
    const sourceWidth = image.naturalWidth;
    const sourceHeight = image.naturalHeight;
    const targetAspectRatio = 16 / 9;

    let cropWidth = sourceWidth;
    let cropHeight = sourceHeight;
    if (sourceWidth / sourceHeight > targetAspectRatio) {
      cropWidth = sourceHeight * targetAspectRatio;
    } else {
      cropHeight = sourceWidth / targetAspectRatio;
    }

    const sourceX = (sourceWidth - cropWidth) / 2;
    const sourceY = (sourceHeight - cropHeight) / 2;
    const outputWidth = Math.round(cropWidth);
    const outputHeight = Math.round(cropHeight);

    const canvas = document.createElement("canvas");
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      return Promise.reject(new Error("CANVAS_CONTEXT_UNAVAILABLE"));
    }

    context.drawImage(image, sourceX, sourceY, cropWidth, cropHeight, 0, 0, outputWidth, outputHeight);

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error("CROP_FAILED"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
      );
    });
  }

  private getOrderValue(image: any): number | null {
    const rawOrder = image?.order ?? image?.Order;
    if (rawOrder == null) {
      return null;
    }
    const order = Number(rawOrder);
    if (Number.isNaN(order) || order < 0 || order >= this.images.length) {
      return null;
    }
    return order;
  }

  private buildListingImageSlots(source: any[]): { paths: string[]; metadata: any[] } {
    const paths = Array(this.images.length).fill("");
    const metadata = Array(this.images.length).fill(null);
    const unplaced: any[] = [];

    (source || []).forEach(image => {
      const order = this.getOrderValue(image);
      if (order != null) {
        paths[order] = image?.storageModel?.storagePath ? `${environment.baseImagePath}/${image.storageModel.storagePath}` : "";
        metadata[order] = image;
      } else {
        unplaced.push(image);
      }
    });

    let nextSlot = 0;
    for (const image of unplaced) {
      const path = image?.storageModel?.storagePath ? `${environment.baseImagePath}/${image.storageModel.storagePath}` : "";
      if (path === "") {
        continue;
      }
      while (nextSlot < paths.length && paths[nextSlot] !== "") {
        nextSlot++;
      }
      if (nextSlot >= paths.length) {
        break;
      }
      paths[nextSlot] = path;
      metadata[nextSlot] = image;
      nextSlot++;
    }

    return { paths, metadata };
  }

  private emitUploadedCount() {
    this.imageUploaded.emit(this.imagePaths.filter(path => path != null && path !== "").length);
  }

  downloadAllPhotos() {
    this.inventoryService.downloadAllPhotos(this.inventoryItem.id).subscribe(() => {});
  }
}
